import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth';
import { UserRole } from '../types/user';

interface AuthContextType {
  isAuthenticated: boolean;
  userRole: UserRole | null;
  userId: string | null;
  isLoading: boolean;
  login: (token: string, userId: string, roleId: number) => void;
  logout: () => void;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status on mount and update state
  const checkAuth = () => {
    const authenticated = authService.isAuthenticated();
    const role = authService.getRole();
    const id = authService.getUserId();
    
    setIsAuthenticated(authenticated);
    setUserRole(role);
    setUserId(id);
    setIsLoading(false);
  };

  // Login function that updates context state
  const login = (token: string, userId: string, roleId: number) => {
    authService.setToken(token, userId, roleId);
    checkAuth(); // Update state after setting token
  };

  // Logout function that clears context state
  const logout = () => {
    authService.clearToken();
    setIsAuthenticated(false);
    setUserRole(null);
    setUserId(null);
    window.location.href = '/login';
  };

  // Check auth status on component mount
  useEffect(() => {
    checkAuth();
  }, []);

  const value = {
    isAuthenticated,
    userRole,
    userId,
    isLoading,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 